import React, { FC, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Journey } from 'src/common/interfaces/journey.interface';
import { STEPS_VIEWED_CARD } from 'src/constants';
import { useCurrentOrganization } from 'src/store/organization';
import { apiGetJourney } from 'src/utils/journeyApi';
import { FixedLoadingSpinner } from 'src/common/components/loading-spinner';
import { BreadcrumbsConfig, MetricCard } from '../../constants';
import { convertEngagementsToMetrics } from '../../helpers/convert-engagements-to-metrics';
import { DashboardInsightsItemLayout } from '../../item-layout';
import { DashboardInsightsJourneysJourneyVisitorContent } from './content';
import { useEngagementsMetrics } from '../use-engagements-metrics.hook';
import { useJourneyInsights } from '../use-journey-insights.hook';
import { DashboardInsightsJourneysJourneyVisitorDetailsSlideover } from './details-slideover';
import { Engagement } from 'src/editor/insights/constants';
import classNames from 'classnames';
import { PreviewRegularIcon } from 'src/monet/icons';
import { createJourneyOpensCard } from '../../helpers/metric-cards';
import { useJourneyMetrics } from '../use-journey-metrics.hook';
import useDashboardActiveTab from 'src/dashboard/helpers/active-tab.hook';
import { EllipsizedText } from 'src/common/components/table/ellipsized-text';
import { DashboardInsightsJourneysTabType } from '../constants';
import { Helmet } from 'react-helmet';
import OrganizationLink from 'src/common/OrganizationLink';
import { useCurrentSubscription } from 'src/store/subscription';
import { useFeatureRestrictions } from 'src/hooks/useFeatureRestrictions';
import { useJourneys } from 'src/store/journeys';

const VISITOR_INSIGHT_CARDS = [STEPS_VIEWED_CARD];

const getEngagementMetrics = (engagements: Engagement[]): MetricCard[] =>
  convertEngagementsToMetrics(VISITOR_INSIGHT_CARDS, engagements);

type DashboardInsightsJourneysJourneyVisitorProps = {
  journeyUuid: string;
  sessionUuid: string;
  activeTabType?: DashboardInsightsJourneysTabType;
};

export const DashboardInsightsJourneysJourneyVisitor: FC<DashboardInsightsJourneysJourneyVisitorProps> = ({
  journeyUuid,
  sessionUuid,
  activeTabType = 'steps',
}) => {
  useDashboardActiveTab('insights');
  const [journey, setJourney] = useState<Journey>();
  const [loading, setLoading] = useState(true);

  const history = useHistory();
  const { currentOrganization } = useCurrentOrganization();
  const { subscriptionPlanType, isOnTrial } = useCurrentSubscription();
  const { features, showUpgradeModal } = useFeatureRestrictions();
  const journeys = useJourneys((state) => state.journeys);
  const engagementsFilters = useMemo(() => ({ session_uuid: sessionUuid }), [sessionUuid]);
  const { fetching: fetchingEngagements, engagements } = useEngagementsMetrics({
    journeyId: journey?.id,
    filters: engagementsFilters,
  });
  const insightsFilters = useMemo(() => ({ session_uuid: sessionUuid }), [sessionUuid]);
  const { fetching: fetchingInsights, visitorViews } = useJourneyInsights({
    journeyUuid: journey?.uuid,
    page: 1,
    filters: insightsFilters,
  });
  const metricsFilters = useMemo(() => ({ session_uuid: sessionUuid }), [sessionUuid]);
  const { fetching: fetchingMetrics, metrics } = useJourneyMetrics({
    journeyId: journey?.id,
    filters: metricsFilters,
  });

  const [isProfileOpen, setIsProfileOpen] = useState(false);

  const visitorView = visitorViews.length > 0 ? visitorViews[0] : undefined;

  const breadcrumbsConfig: BreadcrumbsConfig = {
    items: [
      {
        value: 'Insights',
        onClick: () => {
          history.push(`/${currentOrganization.slug}/insights`);
        },
      },
      {
        value: 'Journeys',
        onClick: () => {
          history.push(`/${currentOrganization.slug}/insights/journeys`);
        },
      },
      {
        value: journey?.name || '',
        onClick: () => {
          history.push(`/${currentOrganization.slug}/insights/journeys/${journey?.uuid}`);
        },
      },
      {
        value: visitorView?.name || 'Anonymous',
      },
    ],
  };

  const fetchJourney = async () => {
    try {
      setLoading(true);
      const fetchedJourney = await apiGetJourney(journeyUuid);
      setJourney(fetchedJourney);
      
      // Check if user can access basic insights (available on all plans)
      if (!features.canAccessBasicInsights) {
        showUpgradeModal(
          'basic_insights',
          'You need access to basic insights to view visitor analytics. Please contact support.',
          'Insights Access Required'
        );
        // Redirect back to journeys list
        history.push(`/${currentOrganization.slug}/journeys`);
        return;
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchJourney();
  }, [journeyUuid]);

  if (loading || fetchingEngagements || fetchingInsights || fetchingMetrics) {
    return <FixedLoadingSpinner />;
  }

  if (!journey) {
    return <div>Cannot find journey</div>;
  }

  const metricCards: MetricCard[] = [
    createJourneyOpensCard(metrics?.num_sessions || 0),
    ...getEngagementMetrics(engagements),
  ];

  const headerSection = (
    <div className='flex flex-col gap-2'>
      <div className='flex items-center gap-4'>
        <EllipsizedText className='text-bedrock-serif-h1' text={visitorView?.name || 'Anonymous'} />
        {visitorView && (
          <div
            className={classNames(
              'transition-opacity shrink-0',
              'flex items-center justify-center px-1.5 py-0.5 rounded',
              'text-bedrock-white text-bedrock-tiny-strong bg-gradient-angular-green capitalize cursor-pointer has-hover'
            )}
            onClick={() => setIsProfileOpen(true)}
          >
            WHO IS THIS?
          </div>
        )}
      </div>
      <div className='flex items-center gap-4'>
        <div className='text-bedrock-black'>
          <PreviewRegularIcon />
        </div>
        <div className='text-bedrock-serif-h2'>viewing {journey.name}</div>
      </div>
      {
      visitorView?.session_replay_uuid &&
      <div className='flex mt-2 '>
        <OrganizationLink
                                className="relative block mt-3 items-center max-w-max mt-4 space-x-1 cursor-pointer text-white bg-gray-700 hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 py-2 px-4 border border-transparent text-base font-semibold rounded-md transition duration-150 ease-in-out"

                    to={`/sessions/${visitorView?.session_replay_uuid}`}
                  >{`View Session Replay`}
        </OrganizationLink>
      </div>
      }
    </div>
  );

  return (
    <>
      <Helmet>
        <title>
          Journey | Insights | Journey | {journey.name} | {visitorView?.name || 'Anonymous'}
        </title>
      </Helmet>
      <DashboardInsightsItemLayout
        headerSection={headerSection}
        breadcrumbsConfig={breadcrumbsConfig}
        metricCards={metricCards}
        contentSection={
          <DashboardInsightsJourneysJourneyVisitorContent
            journey={journey}
            sessionUuid={sessionUuid}
            activeTabType={activeTabType}
          />
        }
      />
      {visitorView && (
        <DashboardInsightsJourneysJourneyVisitorDetailsSlideover
          open={isProfileOpen}
          onClose={() => setIsProfileOpen(false)}
          sessionUuid={sessionUuid}
          visitorView={visitorView}
        />
      )}
    </>
  );
};
